
import React, { useContext, useState } from "react";
import { AuthContext } from "../context/authContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../Components/ui/card";
import { Badge } from "../Components/ui/badge";
import { Button } from "../Components/ui/button";
import { ArrowLeft, MapPin, Calendar, Package, User, Truck, CheckCircle, Clock, Trophy, Target, Users, Anchor, Tag, ShoppingCart, CreditCard, ExternalLink, Gift } from "lucide-react";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "../Components/ui/skeleton";

import ProofGallery from "../Components/donation/ProofGallery";
import NFCTaggingInterface from "../Components/donation/NFCTaggingInterface";

import { useToast } from "../context/ToastContext";

// Status Timeline Components
const TimelineStep = ({ title, date, status, isCompleted, isActive, isLast }) => (
  <div className="flex flex-col relative flex-1">
    <div className="flex items-center">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 z-10 transition-colors ${isCompleted ? 'bg-green-500 border-green-500 text-white' :
        isActive ? 'bg-blue-500 border-blue-500 text-white' :
          'bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-300'
        }`}>
        {isCompleted ? <CheckCircle className="w-5 h-5" /> : isActive ? <Clock className="w-5 h-5" /> : <div className="w-3 h-3 rounded-full bg-zinc-300 dark:bg-zinc-700" />}
      </div>
      {!isLast && (
        <div className={`flex-1 h-1 mx-2 transition-colors ${isCompleted ? 'bg-green-500' : 'bg-zinc-200 dark:bg-zinc-800'
          }`} />
      )}
    </div>
    <div className="mt-2 pr-4">
      <p className={`text-sm font-semibold ${isCompleted ? 'text-green-600 dark:text-green-400' :
        isActive ? 'text-blue-600 dark:text-blue-400' :
          'text-zinc-500'
        }`}>{title}</p>
      {date && <p className="text-xs text-zinc-500 mt-1">{date}</p>}
    </div>
  </div>
);

const statusDetailsColors = {
  pending_approval: "bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800",
  pending: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
  rejected: "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800",
  in_transit: "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800",
  delivered: "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800",
  confirmed: "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800",
  received: "bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800",
  distributed: "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800"
};

export default function DonationDetails() {
  const { user } = useContext(AuthContext);
  const [actionLoading, setActionLoading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const { addToast } = useToast();
  const queryClient = useQueryClient(); // Need this to refresh data
  const navigate = useNavigate();
  // Support both query param ?id= and route param /:id
  const { id } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const donationId = id || urlParams.get('id');

  const { data: donation, isLoading, isError } = useQuery({
    queryKey: ['donation', donationId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/donations/${donationId}`);
      return data;
    },
    enabled: !!donationId
  });

  if (!donationId) return <div className="min-h-screen flex items-center justify-center">Invalid Donation ID</div>;

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 bg-zinc-50 dark:bg-zinc-950">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-12 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !donation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-500">Error Loading Donation</h2>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </div>
    );
  }

  // Helper to determine step status
  const getStepStatus = (stepName) => {
    const status = donation.status;
    if (stepName === 'submitted') {
      return {
        isCompleted: status !== 'pending_approval',
        isActive: status === 'pending_approval'
      };
    }
    if (stepName === 'approved') {
      return {
        isCompleted: status !== 'pending_approval',
        isActive: false
      };
    }
    if (stepName === 'shipped') {
      return {
        isCompleted: !['pending_approval', 'pending', 'in_transit'].includes(status),
        isActive: status === 'pending' || status === 'in_transit'
      };
    }
    if (stepName === 'received') {
      return {
        isCompleted: ['received', 'distributed', 'delivered', 'confirmed'].includes(status),
        isActive: false
      };
    }
    return { isCompleted: false, isActive: false };
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduleDate) return;

    setActionLoading(true);
    try {
      await apiClient.put(`/donations/${donationId}/schedule`, {
        scheduled_delivery: scheduleDate
      });
      queryClient.invalidateQueries(['donation', donationId]);
      addToast('Delivery scheduled successfully!', 'success');
    } catch (err) {
      console.error("Failed to schedule delivery", err);
      const errMsg = err.response?.data?.msg || "Failed to schedule delivery";
      addToast(errMsg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkReceived = async () => {
    // Removed native confirm as per user request to use custom UI feedback
    setActionLoading(true);
    try {
      await apiClient.post(`/admin/donations/${donationId}/receive`);
      // Refresh data
      queryClient.invalidateQueries(['donation', donationId]);
      addToast('Donation marked as received!', 'success');
    } catch (err) {
      console.error("Failed to update status", err);
      addToast("Failed to update status", 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const isFullyTagged = donation.items?.every(item => {
    const taggedCount = item.nfc_ids ? item.nfc_ids.length : 0;
    return taggedCount >= (item.quantity || 0);
  });

  return (
    <div className="min-h-screen p-6 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800">
              <ArrowLeft className="w-6 h-6 text-zinc-600 dark:text-zinc-400" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">Donation Details</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="font-mono text-xs text-zinc-500 px-2 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">
                  #{donation.donationId || donation._id.slice(-6).toUpperCase()}
                </span>
                <span className="text-sm text-zinc-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {format(new Date(donation.createdAt), 'MMM d, yyyy')}
                </span>
              </div>
            </div>
          </div>
          <div>
            <Badge variant="none" className={`text-sm px-4 py-1.5 uppercase tracking-wide font-semibold shadow-sm ${
              statusDetailsColors[donation.status] || 'bg-zinc-50 dark:bg-zinc-900/30 text-zinc-700 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800'
            }`}>
              {donation.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>

        {/* --- Content based on Donation Type --- */}

        {/* 1. GARMENTS VIEW */}
        {donation.donation_type === 'garments' && (
          <div className="grid gap-8">
            {/* Timeline Card */}
            <Card className="border-zinc-200 dark:border-zinc-800 shadow-sm bg-white dark:bg-zinc-900">
              <CardHeader className="pb-4 border-b border-zinc-100 dark:border-zinc-800/50">
                <CardTitle className="text-lg">Donation Status</CardTitle>
              </CardHeader>
              <CardContent className="pt-8">
                <div className="flex flex-col md:flex-row justify-between gap-6 overflow-x-auto">
                  <TimelineStep
                    title="Submitted"
                    date={format(new Date(donation.createdAt), 'MMM d')}
                    {...getStepStatus('submitted')}
                  />

                  {donation.warehouse_info && (
                    <>
                      <TimelineStep
                        title="Approved & Location Assigned"
                        date={donation.status !== 'pending_approval' ? 'Approved' : 'Pending'}
                        {...getStepStatus('approved')}
                      />
                      <TimelineStep
                        title="On the way"
                        date={donation.status === 'in_transit' ? 'Verified Sent' : ''}
                        {...getStepStatus('shipped')}
                      />
                      <TimelineStep
                        title="Received at Warehouse"
                        {...getStepStatus('received')}
                      />
                      {(() => {
                        const totalItems = donation.items?.reduce((acc, item) => acc + (item.quantity || 0), 0) || 0;
                        const totalDelivered = donation.items?.reduce((acc, item) => acc + (item.delivered_count || 0), 0) || 0;
                        const pct = totalItems > 0 ? Math.round((totalDelivered / totalItems) * 100) : 0;
                        const isReached = ['received', 'distributed', 'delivered', 'confirmed'].includes(donation.status);

                        return (
                          <TimelineStep
                            title="Distributed"
                            date={`${pct}% Distributed`}
                            isLast
                            isCompleted={pct === 100 && isReached}
                            isActive={isReached && pct < 100}
                          />
                        );
                      })()}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left/Main Column: Occupies 2/3 of desktop width */}
              <div className="lg:col-span-2 space-y-8">
                {/* Donor Action: Schedule Delivery Date */}
                {user?.role === 'Donor' && donation.status === 'pending' && !donation.scheduled_delivery && donation.warehouse_info && (
                  <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-900/10 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2 text-blue-900 dark:text-blue-300">
                        <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" /> Action Required: Schedule Delivery
                      </CardTitle>
                      <CardDescription className="text-zinc-600 dark:text-zinc-400">
                        Your donation has been approved! Please specify when you will ship or drop off the garments to the assigned warehouse below.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <form onSubmit={handleScheduleSubmit} className="space-y-4 max-w-md">
                        <div className="space-y-2">
                          <label htmlFor="scheduled_delivery_input" className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                            Delivery/Drop-off Date
                          </label>
                          <input
                            id="scheduled_delivery_input"
                            type="date"
                            required
                            min={new Date().toISOString().split('T')[0]}
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                            onClick={(e) => {
                              try {
                                e.currentTarget.showPicker();
                              } catch (err) {
                                console.warn("showPicker is not supported in this browser", err);
                              }
                            }}
                            className="w-full p-2 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={actionLoading || !scheduleDate}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                        >
                          {actionLoading ? <Clock className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                          Confirm Delivery Schedule
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {/* Admin Action: Mark as Received */}
                {user?.role === 'Administrator' && (donation.status === 'pending' || donation.status === 'in_transit') && (
                  <Card className="border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-900/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600" /> Admin Action</CardTitle>
                    </CardHeader>
                    <CardContent className="flex items-center justify-between">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">Has this shipment arrived securely at the warehouse?</p>
                      <Button
                        onClick={handleMarkReceived}
                        disabled={actionLoading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {actionLoading ? <Clock className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
                        Mark as Received at Warehouse
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Admin Action: NFC Tagging (Only after received) */}
                {user?.role === 'Administrator' && donation.status === 'received' && (
                  <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-900/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-blue-600" /> NFC Tag Assignment</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                        Scan and assign NFC tags to the items below. These tags will be tracked during distribution.
                      </p>
                      <NFCTaggingInterface
                        donation={donation}
                        onUpdate={() => queryClient.invalidateQueries(['donation', donationId])}
                        showHeader={false}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Logistics & Fulfillment */}
                {(donation.warehouse_info || donation.scheduled_delivery || donation.address) && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-lg font-semibold text-zinc-800 dark:text-zinc-200">
                      <Truck className="w-5 h-5 text-blue-500" /> Logistics & Fulfillment
                    </div>

                    <div className={`grid gap-6 ${(donation.warehouse_info && (donation.scheduled_delivery || donation.address)) ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
                      {/* Warehouse Location */}
                      {donation.warehouse_info && (
                        <Card className="group hover:border-blue-400 transition-colors duration-300 bg-gradient-to-br from-white to-blue-50/50 dark:from-zinc-900 dark:to-blue-900/10 border-zinc-200 dark:border-zinc-800 h-full flex flex-col justify-between">
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base text-zinc-700 dark:text-zinc-300">
                              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                                <Anchor className="w-4 h-4" />
                              </div>
                              Shipping Destination
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                            <div>
                              <p className="font-bold text-lg text-zinc-900 dark:text-zinc-100">{donation.warehouse_info.name}</p>
                              <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed mt-1">{donation.warehouse_info.address}</p>
                              <div className="flex items-center gap-2 mt-2 text-sm text-zinc-500">
                                <User className="w-3 h-3" /> {donation.warehouse_info.contact}
                              </div>
                            </div>
                            <Button variant="outline" size="sm" className="w-full gap-2 border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 mt-4"
                              onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(donation.warehouse_info.address)}`, '_blank')}
                            >
                              <MapPin className="w-4 h-4" /> View on Maps
                            </Button>
                          </CardContent>
                        </Card>
                      )}

                      {/* Shipping / Drop-off Details */}
                      {(donation.scheduled_delivery || donation.address) && (
                        <Card className="h-full border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-base text-zinc-700 dark:text-zinc-300">
                              <div className="p-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded-lg">
                                <Truck className="w-4 h-4" />
                              </div>
                              Shipping / Drop-off Details
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="space-y-3">
                              <div className="flex items-start gap-3">
                                <Clock className="w-4 h-4 text-zinc-400 mt-1 shrink-0" />
                                <div>
                                  <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Estimated Send Date</p>
                                  <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200 mt-0.5">
                                    {donation.scheduled_delivery
                                      ? format(new Date(donation.scheduled_delivery), 'PPP')
                                      : "Not scheduled yet"}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-zinc-400 mt-1 shrink-0" />
                                <div>
                                  <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Sender Address</p>
                                  <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-0.5">{donation.address || "No address provided"}</p>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                )}

                {/* NFC Imprint Data - Full Width of Main Column */}
                {donation.nfc_info && (
                  <Card className="overflow-hidden border-violet-200 dark:border-violet-800 bg-white dark:bg-zinc-900 shadow-sm">
                    <div className="absolute top-0 left-0 w-1 h-full bg-violet-500" />
                    <CardHeader className="bg-violet-50/30 dark:bg-violet-900/10 pb-4 border-b border-violet-100 dark:border-violet-900/50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-3 text-violet-900 dark:text-violet-300">
                          <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
                            <User className="w-4 h-4" />
                          </div>
                          NFC Imprint Data
                        </CardTitle>
                        <Badge variant="none" className="border-violet-200 text-violet-600 bg-violet-50 dark:border-violet-800 dark:text-violet-400 dark:bg-violet-950/20">
                          Action Required
                        </Badge>
                      </div>
                      <CardDescription className="ml-11">Provide these details to the NFC dealer for programming.</CardDescription>
                    </CardHeader>
                    <CardContent className="grid md:grid-cols-3 gap-6 pt-6">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-zinc-500 uppercase">Donor Name</p>
                        <p className="font-semibold text-lg text-zinc-900 dark:text-zinc-100">{donation.donor?.name || 'Anonymous'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-zinc-500 uppercase">Contact</p>
                        <p className="text-zinc-700 dark:text-zinc-300 font-medium">{donation.donor?.email}</p>
                        <p className="text-sm text-zinc-500">{donation.donor?.phone_number}</p>
                      </div>
                      <div className="space-y-2 bg-violet-50 dark:bg-violet-900/20 p-3 rounded-lg border border-violet-100 dark:border-violet-800/50">
                        <p className="text-xs font-medium text-violet-600 dark:text-violet-400 uppercase flex items-center gap-1">
                          <Gift className="w-3 h-3" /> Wish Message
                        </p>
                        <p className="italic text-zinc-700 dark:text-zinc-300 text-sm">"{donation.wish_message || 'Best Wishes!'}"</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar Column: Occupies 1/3 of desktop width */}
              <div className="space-y-8">
                {/* Donation Items list */}
                <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm h-fit">
                  <CardHeader className="pb-3 border-b border-zinc-100 dark:border-zinc-800">
                    <CardTitle className="flex items-center gap-2 text-base text-zinc-800 dark:text-zinc-200">
                      <div className="p-2 bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400 rounded-lg">
                        <Package className="w-4 h-4" />
                      </div>
                      Donation Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ul className="space-y-3">
                      {donation.items?.map((item, idx) => (
                        <li key={idx} className="flex justify-between items-center p-3 bg-zinc-50 dark:bg-zinc-950/50 rounded-lg border border-zinc-100 dark:border-zinc-800/30">
                          <div>
                            <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">{item.name}</p>
                            <p className="text-xs text-zinc-500 capitalize mt-0.5">{item.category}</p>
                            {item.nfc_ids && item.nfc_ids.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.nfc_ids.map((tagId, tagIdx) => (
                                  <Badge key={tagIdx} variant="none" className="text-[10px] h-5 bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400 border border-violet-100 dark:border-violet-900/50 font-mono flex items-center gap-1" title={tagId}>
                                    <Tag className="w-3 h-3 text-violet-500" />
                                    {tagId.length > 8 ? `...${tagId.slice(-8)}` : tagId}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Badge variant="outline" className="shrink-0">{item.quantity} units</Badge>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* NFC Dealer Info - Shows only if NOT fully tagged */}
                {donation.nfc_info && !isFullyTagged && (
                  <Card className="group hover:border-amber-400 transition-colors duration-300 bg-gradient-to-br from-white to-amber-50/50 dark:from-zinc-900 dark:to-amber-900/10 border-zinc-200 dark:border-zinc-800 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base text-zinc-700 dark:text-zinc-300">
                        <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                          <Tag className="w-4 h-4" />
                        </div>
                        NFC Tags Required
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-start p-3 bg-white/50 dark:bg-black/20 rounded-lg border border-amber-100 dark:border-amber-900/50">
                        <div>
                          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Nearest Dealer</p>
                          <p className="font-semibold text-zinc-900 dark:text-zinc-100">{donation.nfc_info.dealer_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Cost</p>
                          <p className="font-bold text-amber-600 text-lg">₹{donation.nfc_info.total_cost}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {donation.nfc_info.amazon_link && (
                          <Button className="w-full bg-[#FF9900]/90 hover:bg-[#FF9900] text-white shadow-sm border-none h-9 text-xs font-medium"
                            onClick={() => window.open(donation.nfc_info.amazon_link, '_blank')}>
                            <ShoppingCart className="w-3 h-3 mr-2" /> Amazon
                          </Button>
                        )}
                        {donation.nfc_info.flipkart_link && (
                          <Button className="w-full bg-[#2874f0]/90 hover:bg-[#2874f0] text-white shadow-sm border-none h-9 text-xs font-medium"
                            onClick={() => window.open(donation.nfc_info.flipkart_link, '_blank')}>
                            <ExternalLink className="w-3 h-3 mr-2" /> Flipkart
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 2. MONEY VIEW */}
        {donation.donation_type === 'money' && (
          <div className="grid gap-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Contribution Card */}
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-900/10 border-green-200 dark:border-green-800/30">
                <CardHeader>
                  <CardTitle className="text-green-800 dark:text-green-400">Contribution Summary</CardTitle>
                  <CardDescription>Thank you for your generosity!</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-5xl font-bold text-green-700 dark:text-green-400 mb-2">
                    ${donation.amount?.toLocaleString()}
                  </div>
                  <p className="text-green-600 dark:text-green-500">
                    Donated on {format(new Date(donation.createdAt), 'MMMM do, yyyy')}
                  </p>
                </CardContent>
              </Card>

              {/* Pool Information (if applicable) */}
              {donation.pool ? (
                <Card className="relative overflow-hidden border-blue-200 dark:border-blue-900">
                  {/* Top Contributor Badge */}
                  {donation.isTopContributor && (
                    <div className="absolute top-0 right-0 p-4">
                      <Badge className="bg-yellow-400 text-yellow-900 hover:bg-yellow-500 border-yellow-500 shadow-lg gap-1">
                        <Trophy className="w-3 h-3" /> Top Contributor
                      </Badge>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-500" />
                      {donation.pool.title}
                    </CardTitle>
                    <CardDescription>This donation supports this automated pool</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-zinc-600 dark:text-zinc-400">Pool Progress</span>
                        <span className="font-medium">{Math.round((donation.pool.current_amount / donation.pool.target_amount) * 100)}%</span>
                      </div>
                      <div className="h-3 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${Math.min(100, (donation.pool.current_amount / donation.pool.target_amount) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center bg-zinc-50 dark:bg-zinc-900 p-3 rounded-lg">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase">Pool Target</p>
                        <p className="font-semibold">${donation.pool.target_amount?.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-zinc-500 uppercase">Total Raised</p>
                        <p className="font-semibold text-blue-600">${donation.pool.current_amount?.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="text-sm text-zinc-500 text-center pt-2">
                      You are contributor <b>#{donation.poolRank}</b> out of <b>{donation.poolTotalDonors}</b> donors!
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="flex items-center justify-center h-full text-zinc-500">
                    This donation was not assigned to a specific pool.
                  </CardContent>
                </Card>
              )}
            </div>

            {/* [ADMIN ONLY] Pool Donors List */}
            {donation.poolDonationsList && donation.poolDonationsList.length > 0 && (
              <Card className="mt-8 border-violet-200 dark:border-violet-900/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-500" />
                    Pool Donors (Admin View)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border border-zinc-200 dark:border-zinc-800">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-zinc-500 dark:text-zinc-400">Donor Name</th>
                          <th className="px-4 py-3 text-left font-medium text-zinc-500 dark:text-zinc-400">Amount</th>
                          <th className="px-4 py-3 text-right font-medium text-zinc-500 dark:text-zinc-400">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                        {donation.poolDonationsList.map((d) => (
                          <tr key={d._id} className={d._id === donation._id ? "bg-blue-50/50 dark:bg-blue-900/10" : ""}>
                            <td className="px-4 py-3 font-medium">
                              {d.donorName}
                              {d._id === donation._id && <Badge variant="none" className="ml-2 text-xs border-blue-200 text-blue-600 dark:border-blue-800 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20">Current</Badge>}
                            </td>
                            <td className="px-4 py-3 text-green-600 dark:text-green-400 font-medium">
                              ${d.amount.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-zinc-500">
                              {format(new Date(d.date), 'MMM d, yyyy')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Proof Gallery for all types */}
        {(donation.proof_sent || (donation.proofs && donation.proofs.length > 0)) && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-4">Impact Proof</h2>
            <ProofGallery proofs={donation.proofs || []} donationId={donation._id} />
          </div>
        )}

      </div>
    </div>
  );
}
